import type {ChangeEvent, ReactElement} from 'react';
import {useContext, useEffect, useRef, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {useComponentAttribute} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {resolveCountdownLabel, setCountdownAttribute} from '@EmailMakerReact/lib/email-maker-countdown-library';
import {COUNTDOWN_UNIT_DEFAULT_LABEL_KEYS, COUNTDOWN_UNIT_LABEL_ATTRIBUTES} from '@EmailMakerReact/lib/email-maker-countdown-library.types';
import type {CountdownLabelFieldProps, PendingLabelWrite} from './countdown-label-field.types';
import {COUNTDOWN_LABEL_MAX_LENGTH, COUNTDOWN_LABEL_WRITE_DEBOUNCE_MS} from './countdown-label-field.types';
import styles from './countdown-label-field.module.scss';

export function CountdownLabelField({component, unit}: CountdownLabelFieldProps): ReactElement {
  const {editor} = useContext(emailMakerContext);
  const attribute = COUNTDOWN_UNIT_LABEL_ATTRIBUTES[unit];
  const liveValue = useComponentAttribute(component, attribute);
  const [inputValue, setInputValue] = useState<string>(resolveCountdownLabel(liveValue, unit));
  const pendingWriteRef = useRef<PendingLabelWrite | null>(null);

  useEffect(() => {
    setInputValue(resolveCountdownLabel(liveValue, unit));
  }, [unit, liveValue]);

  useEffect(() => {
    return flushPendingWrite;
  }, [component]);

  function flushPendingWrite(): void {
    const pending = pendingWriteRef.current;
    if (pending === null) {
      return;
    }

    clearTimeout(pending.timeout);
    pendingWriteRef.current = null;
    if (editor) {
      setCountdownAttribute(editor, pending.component, attribute, pending.value);
    }
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    const raw = event.target.value;
    setInputValue(raw);

    if (pendingWriteRef.current !== null) {
      clearTimeout(pendingWriteRef.current.timeout);
    }

    pendingWriteRef.current = {component, value: raw.trim(), timeout: setTimeout(flushPendingWrite, COUNTDOWN_LABEL_WRITE_DEBOUNCE_MS)};
  }

  const fieldLabel = window.i18next.t(COUNTDOWN_UNIT_DEFAULT_LABEL_KEYS[unit]);

  return (
    <label className={styles.field}>
      <Text val={fieldLabel} size={TextSize.SM} className={styles.fieldLabel} />
      <div className={styles.inputContainer}>
        <input aria-label={fieldLabel} className={styles.input} maxLength={COUNTDOWN_LABEL_MAX_LENGTH} onChange={handleChange} type="text" value={inputValue} />
      </div>
    </label>
  );
}
